import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import ProfileCard from "@/components/ProfileCard";
import { ShieldCheck, Lock, BarChart3, Sparkles, Globe, ArrowRight, Star, Quote, Users } from "lucide-react";

const HERO_BG_WIDE = "https://customer-assets.emergentagent.com/job_companion-directory/artifacts/gmsolnim_photo_2026-07-09_22-23-28.jpg";
const HERO_BG_TALL = "https://customer-assets.emergentagent.com/job_companion-directory/artifacts/gu4rxiqb_photo_2026-07-09_22-23-27.jpg";
const AGENCY_BG = "https://images.unsplash.com/photo-1729605412184-8d796f9c6f66?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

// Bespoke burgundy for the ESCORT accent + primary CTA.
const BURGUNDY = "#98163A";      // accent text (ESCORT title, trust icons)
const BURGUNDY_BTN = "#98163A";  // See profiles button base
const BURGUNDY_BTN_HOVER = "#B51F49";
const BURGUNDY_DEEP = "#7A102D"; // deep tone for hover states / borders

const TESTIMONIALS = [
  { name: "Marco D.", role: "Member, Tivat", q: "The verification standards are unlike anything else in the region. Exactly what I expected from a premium service." },
  { name: "Andrei K.", role: "Member, Budva", q: "Discreet, refined and trustworthy. The trust score is genuinely useful." },
  { name: "Stefan P.", role: "Member, Kotor", q: "Beautiful interface, real verification, and the credit system keeps things fair." },
];

export default function Home() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [agencies, setAgencies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [co, c, f, a] = await Promise.all([
          api.get("/meta/countries"),
          api.get("/meta/cities"),
          api.get("/profiles/featured", { params: { limit: 8 } }),
          api.get("/agencies/featured", { params: { limit: 3 } }),
        ]);
        setCountries(co.data.countries);
        setCities(c.data.cities);
        setFeatured(f.data.profiles);
        setAgencies(a.data.agencies);
      } catch (e) { console.warn("[Home.jsx] non-blocking error:", e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!country) return;
      try {
        const { data } = await api.get("/meta/cities", { params: { country_code: country } });
        setCities(data.cities);
        setCity("");
      } catch (e) { console.warn("[Home.jsx] non-blocking error:", e); }
    })();
  }, [country]);

  const goSearch = () => {
    const params = new URLSearchParams();
    if (country) params.set("country", country);
    if (city) params.set("city", city);
    const qs = params.toString();
    navigate(qs ? `/directory?${qs}` : "/directory");
  };

  const CITY_IMAGES = {
    Budva: "https://images.unsplash.com/photo-1693744412257-673b45083cf4?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Podgorica: "https://images.pexels.com/photos/10816207/pexels-photo-10816207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    Tivat: "https://images.unsplash.com/photo-1729605412184-8d796f9c6f66?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Kotor: "https://images.unsplash.com/photo-1693744412257-673b45083cf4?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  };
  const DEFAULT_CITY_IMAGE = "https://images.unsplash.com/photo-1693744412257-673b45083cf4?crop=entropy&cs=srgb&fm=jpg&w=900&q=80";
  // Show only the 4 highest-traffic cities on the home page
  const popularCities = [...cities].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a0a0a]" data-testid="home-hero">
        {/* Desktop/tablet: WIDE image as CSS background, right-anchored so the
            model composition sits on the right, copy overlays the left fade.
            Mobile: TALL image as CSS background, full-bleed portrait framing.
            Each background is `md:hidden` / `hidden md:block` so exactly ONE
            hero image renders at any viewport. No <img> element = no duplicate. */}
        <div
          className="absolute inset-0 md:hidden bg-center bg-cover"
          style={{ backgroundImage: `url("${HERO_BG_TALL}")` }}
          data-testid="hero-bg-mobile"
        />
        <div
          className="absolute inset-0 hidden md:block bg-center bg-cover"
          style={{ backgroundImage: `url("${HERO_BG_WIDE}")`, backgroundPosition: "right center" }}
          data-testid="hero-bg-desktop"
        />
        {/* Overlays */}
        <div className="absolute inset-0 md:hidden bg-[#0a0a0a]/55" />
        <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/85 via-40% to-transparent" />
        <div className="hidden md:block absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/50 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 md:pt-24 md:pb-16 min-h-[92vh] md:min-h-[88vh] flex items-center">
          <div className="w-full md:w-1/2 lg:max-w-xl">
            {/* Eyebrow */}
            <p className="text-[10.5px] sm:text-xs tracking-[0.32em] uppercase text-white/80 mb-4 md:mb-7 animate-fade-up opacity-0" data-testid="hero-eyebrow">
              Verified <span className="text-white/40 mx-1.5">•</span> Private <span className="text-white/40 mx-1.5">•</span> 18+
            </p>

            {/* Title */}
            <h1 className="font-heading font-light leading-[0.95] tracking-tight animate-fade-up opacity-0" style={{ animationDelay: "0.15s" }} data-testid="hero-title">
              <span className="block text-[52px] sm:text-6xl lg:text-7xl" style={{ color: BURGUNDY }}>ESCORT</span>
              <span className="block text-[52px] sm:text-6xl lg:text-7xl text-white -mt-1">PLANET</span>
            </h1>

            <p className="text-[#d4d4d4] text-sm sm:text-base lg:text-[17px] max-w-md mt-4 md:mt-6 leading-relaxed animate-fade-up opacity-0" style={{ animationDelay: "0.3s" }} data-testid="hero-subtitle">
              Browse verified independent profiles and agencies.
            </p>

            {/* Selectors — vertical stack on mobile, horizontal row from md+ */}
            <div className="mt-6 md:mt-9 grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-2.5 md:gap-3 animate-fade-up opacity-0" style={{ animationDelay: "0.45s" }}>
              <div className="relative">
                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70 pointer-events-none" strokeWidth={1.5} />
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  aria-label="Choose country"
                  className="w-full appearance-none bg-black/60 backdrop-blur-md border border-white/15 text-white text-sm pl-10 pr-8 py-3.5 rounded-md focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/10"
                  data-testid="hero-country-select"
                >
                  <option value="">All countries</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name_en}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              <div className="relative">
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="10" r="2.5" /></svg>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  aria-label="Choose city"
                  className="w-full appearance-none bg-black/60 backdrop-blur-md border border-white/15 text-white text-sm pl-10 pr-8 py-3.5 rounded-md focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/10"
                  data-testid="hero-city-select"
                >
                  <option value="">Choose a city</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.name}>{c.name} ({c.count})</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              <button
                onClick={goSearch}
                className="w-full md:w-auto rounded-md px-6 md:px-8 py-3.5 text-sm font-medium text-white flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0a0a0a] focus:ring-white/40 transition-colors"
                style={{ backgroundColor: BURGUNDY_BTN }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BURGUNDY_BTN_HOVER)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BURGUNDY_BTN)}
                data-testid="hero-search-btn"
              >
                See profiles <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>

            {/* Trust row */}
            <div className="mt-5 md:mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-[13px] text-[#d4d4d4] animate-fade-up opacity-0" style={{ animationDelay: "0.6s" }} data-testid="hero-trust-row">
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" strokeWidth={1.8} style={{ color: BURGUNDY }} aria-hidden="true" />
                Identity verified profiles
              </span>
              <span className="flex items-center gap-2">
                <Lock className="w-4 h-4" strokeWidth={1.8} style={{ color: BURGUNDY }} aria-hidden="true" />
                Privacy protected
              </span>
            </div>
          </div>
        </div>

        {/* Lower info bar — 2x2 on mobile, 4-up on desktop. All icons burgundy per reference. */}
        <div className="relative border-t border-white/5 bg-black/70 backdrop-blur-md" data-testid="hero-info-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: Users,       stat: "10,000+",           label: "Verified profiles" },
              { icon: ShieldCheck, stat: "500+",              label: "Trusted agencies"  },
              { icon: Globe,       stat: "All countries",     label: "Worldwide"         },
              { icon: Lock,        stat: "Privacy protected", label: "Safe browsing"     },
            ].map(({ icon: Icon, stat, label }) => (
              <div key={label} className="flex items-center gap-3 sm:gap-4" data-testid={`hero-info-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="w-6 h-6 sm:w-7 sm:h-7 flex-none" strokeWidth={1.5} style={{ color: BURGUNDY }} aria-hidden="true" />
                <div className="min-w-0">
                  <p className="font-heading text-base sm:text-lg text-white leading-tight truncate">{stat}</p>
                  <p className="text-[11px] sm:text-xs text-white/60 leading-tight mt-0.5 truncate">{label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="featured-section">
        <div className="flex items-end justify-between mb-8">
          <p className="text-overline">{t.home.featured_title}</p>
          <Link to="/directory" className="text-sm text-gold hover:text-gold/80 hidden sm:inline-flex items-center gap-1.5" data-testid="featured-view-all">
            View all <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-5 stagger">
          {featured.map((p) => <ProfileCard key={p.id} profile={p} />)}
        </div>
      </section>

      {/* AGENCIES */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="agencies-section">
        <div className="absolute inset-0">
          <img src={AGENCY_BG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-overline mb-8">{t.home.agencies_title}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {agencies.map((a) => (
              <Link key={a.id} to={`/agency/${a.id}`} className="card-luxe rounded-lg p-6 block group" data-testid={`agency-card-${a.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-overline text-gold">{a.city}</span>
                  {a.verification_level === "agency_verified" && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 border border-emerald/40 px-2 py-0.5 rounded-full">
                      <ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> Agency Verified
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-2xl text-white group-hover:text-gold transition-colors">{a.name}</h3>
                <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">{a.description}</p>
                <div className="mt-6 flex items-center gap-2 text-xs text-gold">
                  View agency <ArrowRight className="w-3 h-3" strokeWidth={2} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="why-section">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-overline">{t.home.why_title}</p>
          <h2 className="font-heading text-4xl sm:text-5xl text-white mt-2 font-light tracking-tight">{t.home.why_sub}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.home.why_pts.map((p, i) => (
            <div key={p.t} className="card-luxe rounded-lg p-6" data-testid={`why-item-${i}`}>
              <div className="w-10 h-10 rounded-md bg-gold/10 flex items-center justify-center mb-4">
                {i === 0 && <ShieldCheck className="w-5 h-5 text-gold" strokeWidth={1.5} />}
                {i === 1 && <Lock className="w-5 h-5 text-gold" strokeWidth={1.5} />}
                {i === 2 && <BarChart3 className="w-5 h-5 text-gold" strokeWidth={1.5} />}
                {i === 3 && <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.5} />}
              </div>
              <h3 className="font-heading text-xl text-white">{p.t}</h3>
              <p className="text-sm text-[#a3a3a3] mt-2 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="reviews-section">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-overline">{t.home.reviews_title}</p>
          <h2 className="font-heading text-4xl sm:text-5xl text-white mt-2 font-light tracking-tight">Quiet praise from members</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((tm, i) => (
            <div key={tm.name} className="card-luxe rounded-lg p-7 relative" data-testid={`testimonial-${i}`}>
              <Quote className="absolute top-5 right-5 w-6 h-6 text-gold/30" strokeWidth={1} />
              <div className="flex gap-0.5 mb-4">
                {[1, 2, 3, 4, 5].map((n) => <Star key={`tm-${tm.name}-s${n}`} className="w-3.5 h-3.5 text-gold fill-gold" strokeWidth={0} />)}
              </div>
          <p className="font-heading text-lg text-[#e5e5e5] leading-relaxed">&ldquo;{tm.q}&rdquo;</p>
              <div className="mt-5 pt-5 border-t border-white/5">
                <p className="text-sm text-white">{tm.name}</p>
                <p className="text-xs text-[#737373]">{tm.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* JOIN */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="join-section">
        <div className="text-center mb-12">
          <p className="text-overline">{t.home.join_title}</p>
          <h2 className="font-heading text-4xl sm:text-5xl text-white mt-2 font-light tracking-tight">{t.home.join_sub}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { role: "client", title: t.home.join_client, desc: "Browse, favorite, review verified profiles." },
            { role: "independent", title: t.home.join_independent, desc: "Manage your own profile, photos and rates." },
            { role: "agency", title: t.home.join_agency, desc: "Run multiple verified profiles under one brand." },
          ].map((j, i) => (
            <Link
              key={j.role}
              to={`/register?role=${j.role}`}
              className={`block rounded-lg p-8 transition-all ${i === 1 ? "bg-gradient-to-b from-[#1a1a1a] to-[#121212] border border-gold/30 hover:border-gold/60" : "card-luxe"}`}
              data-testid={`join-${j.role}`}
            >
              <h3 className="font-heading text-2xl text-white">{j.title}</h3>
              <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">{j.desc}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-gold">
                Get started <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
